import React, { useState, useEffect, useContext, useCallback } from "react";
import { AuthContext } from "../App";
import { Spin, message } from "antd";
import { colors as BRAND } from "../theme";
import {
  cardStyle,
  labelStyle,
  inputStyle,
  btnPrimary,
  btnSecondary,
  btnDisabled,
  bottomNav as bottomBarStyle,
} from "../styles";
import { setupMobileViewport } from "../utils/viewport";
import { apiCall } from "../utils/api";

const API_URL = window.location.origin;

setupMobileViewport();

// ─── Step Indicator ──────────────────────────────────────────────
const STEPS = [
  { key: "container", label: "Container" },
  { key: "service", label: "Service" },
  { key: "plan", label: "Plan" },
  { key: "review", label: "Summary" },
];

function StepIndicator({ current }) {
  return (
    <div style={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "16px 8px 12px",
    }}>
      {STEPS.map((s, i) => {
        const isActive = i === current;
        const isDone = i < current;
        return (
          <React.Fragment key={s.key}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", flex: "0 0 auto" }}>
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 14,
                  fontWeight: 600,
                  background: isDone ? BRAND.success : isActive ? BRAND.primary : "#e5e7eb",
                  color: isDone || isActive ? "#fff" : BRAND.textMuted,
                  transition: "all 0.2s",
                }}
              >
                {isDone ? "\u2713" : i + 1}
              </div>
              <span
                style={{
                  marginTop: 4,
                  fontSize: 11,
                  fontWeight: isActive ? 700 : 400,
                  color: isActive ? BRAND.text : BRAND.textMuted,
                }}
              >
                {s.label}
              </span>
            </div>
            {i < STEPS.length - 1 && (
              <div
                style={{
                  flex: 1,
                  height: 2,
                  background: isDone ? BRAND.success : "#e5e7eb",
                  margin: "0 6px",
                  marginBottom: 18,
                }}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// Shared styles (cardStyle, labelStyle, inputStyle, btnPrimary, btnSecondary,
// btnDisabled, bottomBarStyle) are imported from ../styles at the top of the
// file. Keep using the same identifiers throughout the file.

// ─── Step 1: Select Container ────────────────────────────────────
function StepContainer({ orgId, orgName, onSelect }) {
  const [showWithoutWO, setShowWithoutWO] = useState(true);
  const [search, setSearch] = useState("");
  const [containers, setContainers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [sortCol, setSortCol] = useState("searchkey");
  const [sortDir, setSortDir] = useState("asc");
  const [hasContainersWithoutWO, setHasContainersWithoutWO] = useState(null); // null = not yet loaded
  const [validating, setValidating] = useState(false);

  const fetchContainers = useCallback(async () => {
    if (!orgId) return; // Wait for org to be resolved before fetching
    setLoading(true);
    try {
      const params = new URLSearchParams({ orgId });
      if (showWithoutWO) params.set("withoutWO", "true");
      if (search.trim()) params.set("search", search.trim());
      const data = await apiCall(`/api/mwo/containers?${params}`);
      setContainers(data.containers || []);
      if (data.hasContainersWithoutWO !== undefined) {
        setHasContainersWithoutWO(data.hasContainersWithoutWO);
      }
    } catch (err) {
      message.error("Failed to load containers: " + err.message);
    } finally {
      setLoading(false);
    }
  }, [orgId, showWithoutWO, search]);

  useEffect(() => {
    fetchContainers();
  }, [fetchContainers]);

  // Default checkbox based on whether containers without WO exist (only on first load)
  useEffect(() => {
    if (hasContainersWithoutWO === null) return; // wait for first API response
    if (!hasContainersWithoutWO) {
      setShowWithoutWO(false);
    }
  }, [hasContainersWithoutWO]);

  const sorted = [...containers].sort((a, b) => {
    const av = (a[sortCol] || "").toString().toLowerCase();
    const bv = (b[sortCol] || "").toString().toLowerCase();
    return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
  });

  const toggleSort = (col) => {
    if (sortCol === col) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortCol(col);
      setSortDir("asc");
    }
  };

  const handleSelect = async (container) => {
    setSelected(container.id);
    setValidating(true);
    try {
      // Validate: multi-compartment check
      if (container.compartments > 1) {
        message.warning(
          "To create an Order for a Container with more than 1 compartment, use the back office Sales Order Wizard.",
          5
        );
        setSelected(null);
        setValidating(false);
        return;
      }
      // Validate: facility + active WO warnings (TSP-84)
      const validation = await apiCall("/api/mwo/validate-container", {
        method: "POST",
        body: JSON.stringify({ containerId: container.id, orgId }),
      });
      if (validation.differentFacilityWarning) {
        const ok = window.confirm(
          "This container is at a different facility. Do you want to continue?"
        );
        if (!ok) {
          setSelected(null);
          setValidating(false);
          return;
        }
      }
      if (validation.activeWOWarning) {
        const ok = window.confirm(
          "This container already has an active Work Order at this facility. Do you want to continue?"
        );
        if (!ok) {
          setSelected(null);
          setValidating(false);
          return;
        }
      }
      // Fetch service types for the container
      const svcTypes = await apiCall(`/api/mwo/service-types/${container.id}`);
      onSelect({
        ...container,
        ownerTerminalId: validation.ownerTerminalId || null,
        ownerTerminalName: validation.ownerTerminalName || null,
        serviceTypes: svcTypes.serviceTypes || [],
        defaultServiceTypeId: svcTypes.defaultServiceTypeId || null,
        foodGradeAllowed: svcTypes.foodGradeAllowed || false,
        foodGradeContainerType: svcTypes.foodGradeContainerType || false,
        kosherAllowed: svcTypes.kosherAllowed || false,
        containerCategoryId: svcTypes.containerCategoryId || null,
      });
    } catch (err) {
      message.error("Validation failed: " + err.message);
      setSelected(null);
    } finally {
      setValidating(false);
    }
  };

  const cols = [
    { key: "searchkey", label: "Container ID", width: "30%" },
    { key: "owner", label: "Owner", width: "40%" },
    { key: "containerType", label: "Type", width: "30%" },
  ];

  return (
    <div>
      <div style={cardStyle}>
        <label style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          fontSize: 14,
          minHeight: 44,
          cursor: "pointer",
          WebkitTapHighlightColor: "transparent",
        }}>
          <input
            type="checkbox"
            checked={showWithoutWO}
            onChange={(e) => {
              setShowWithoutWO(e.target.checked);
              setSelected(null);
            }}
            style={{ width: 22, height: 22, flexShrink: 0 }}
          />
          <span style={{ fontWeight: 500, color: BRAND.text }}>
            Show only containers at {orgName} without a work order
          </span>
        </label>
      </div>

      <div style={{ marginBottom: 12 }}>
        <input
          type="search"
          placeholder="Search containers..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setSelected(null);
          }}
          style={inputStyle}
        />
      </div>

      {loading || validating ? (
        <div style={{ textAlign: "center", padding: 40 }}>
          <Spin size="large" />
          <div style={{ marginTop: 8, color: BRAND.textSecondary }}>
            {validating ? "Validating container..." : "Loading containers..."}
          </div>
        </div>
      ) : (
        <div style={{ ...cardStyle, padding: 0, overflow: "hidden" }}>
          {/* Table Header */}
          <div
            style={{
              display: "flex",
              background: BRAND.primary,
              padding: "10px 14px",
            }}
          >
            {cols.map((c) => (
              <div
                key={c.key}
                onClick={() => toggleSort(c.key)}
                style={{
                  width: c.width,
                  color: "#fff",
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: "pointer",
                  userSelect: "none",
                  minHeight: 28,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {c.label} {sortCol === c.key ? (sortDir === "asc" ? "\u25B2" : "\u25BC") : ""}
              </div>
            ))}
          </div>
          {/* Container Cards (mobile-friendly) - scrollable, 7 rows visible */}
          {sorted.length === 0 ? (
            <div style={{ padding: 24, textAlign: "center", color: BRAND.textMuted }}>
              No containers found
            </div>
          ) : (
          <div style={{ maxHeight: 350, overflowY: "auto" }}>
            {sorted.slice(0, 100).map((c) => (
              <div
                key={c.id}
                onClick={() => handleSelect(c)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  padding: "14px",
                  minHeight: 52, // 48px+ touch target
                  borderBottom: `1px solid ${BRAND.border}`,
                  background: selected === c.id ? "#e0f2e9" : "#fff",
                  cursor: "pointer",
                  WebkitTapHighlightColor: "transparent",
                }}
              >
                <div style={{ width: "30%", fontSize: 14, fontWeight: 600, color: BRAND.primary }}>
                  {c.searchkey}
                </div>
                <div style={{ width: "40%", fontSize: 13, color: BRAND.text, paddingRight: 4 }}>{c.owner}</div>
                <div style={{ width: "30%", fontSize: 12, color: BRAND.textSecondary }}>
                  {c.containerType}
                </div>
              </div>
            ))}
          </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Step 2: Service Type & Content ──────────────────────────────
function StepService({ container, orgId, onNext, onBack }) {
  const [serviceTypeId, setServiceTypeId] = useState(container.defaultServiceTypeId || "");
  const [contentId, setContentId] = useState("");
  const [contentSearch, setContentSearch] = useState("");
  const [contents, setContents] = useState([]);
  const [loadingContents, setLoadingContents] = useState(false);
  const [foodGrade, setFoodGrade] = useState(container.foodGradeContainerType || false);
  const [kosher, setKosher] = useState(false);

  // Fetch contents on search
  useEffect(() => {
    if (contentSearch.length < 2) {
      setContents([]);
      return;
    }
    const timer = setTimeout(async () => {
      setLoadingContents(true);
      try {
        const params = new URLSearchParams({ search: contentSearch, orgId, limit: "30" });
        const data = await apiCall(`/api/mwo/contents?${params}`);
        setContents(data.contents || []);
      } catch (err) {
        message.error("Failed to load contents");
      } finally {
        setLoadingContents(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [contentSearch, orgId]);

  const canNext = serviceTypeId && contentId;

  return (
    <div>
      {/* Service Type Radio */}
      <div style={cardStyle}>
        <label style={labelStyle}>Service Type</label>
        {container.serviceTypes.map((st) => (
          <label
            key={st.id}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              padding: "12px 0",
              minHeight: 48,
              borderBottom: `1px solid ${BRAND.border}`,
              cursor: "pointer",
              WebkitTapHighlightColor: "transparent",
            }}
          >
            <input
              type="radio"
              name="serviceType"
              value={st.id}
              checked={serviceTypeId === st.id}
              onChange={() => setServiceTypeId(st.id)}
              style={{ width: 22, height: 22, flexShrink: 0 }}
            />
            <span style={{ fontSize: 16, fontWeight: serviceTypeId === st.id ? 600 : 400 }}>
              {st.name}
            </span>
          </label>
        ))}
      </div>

      {/* Content Selection */}
      <div style={cardStyle}>
        <label style={labelStyle}>Content (Prior 1)</label>
        {contentId ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "12px 14px",
              minHeight: 48,
              background: "#e0f2e9",
              borderRadius: 8,
              marginBottom: 8,
            }}
          >
            <span style={{ fontWeight: 500, fontSize: 15 }}>
              {contents.find((c) => c.id === contentId)?.name || contentSearch}
            </span>
            <span
              onClick={() => {
                setContentId("");
                setContentSearch("");
              }}
              style={{
                color: BRAND.error,
                cursor: "pointer",
                fontSize: 14,
                fontWeight: 500,
                padding: "4px 8px",
                minWidth: 60,
                textAlign: "center",
              }}
            >
              Change
            </span>
          </div>
        ) : (
          <>
            <input
              type="search"
              placeholder="Search for content/product..."
              value={contentSearch}
              onChange={(e) => setContentSearch(e.target.value)}
              style={inputStyle}
            />
            {loadingContents && (
              <div style={{ textAlign: "center", padding: 12 }}>
                <Spin size="small" />
              </div>
            )}
            {!loadingContents && contents.length > 0 && (
              <div
                style={{
                  maxHeight: 200,
                  overflow: "auto",
                  border: `1px solid ${BRAND.border}`,
                  borderRadius: 8,
                  marginTop: 4,
                }}
              >
                {contents.map((c) => (
                  <div
                    key={c.id}
                    onClick={() => {
                      setContentId(c.id);
                      setContentSearch(c.name);
                    }}
                    style={{
                      padding: "12px 14px",
                      minHeight: 48,
                      display: "flex",
                      alignItems: "center",
                      borderBottom: `1px solid ${BRAND.border}`,
                      cursor: "pointer",
                      fontSize: 15,
                      WebkitTapHighlightColor: "transparent",
                    }}
                  >
                    {c.name}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Food Grade / Kosher flags */}
      {(container.foodGradeAllowed || container.kosherAllowed) && (
        <div style={cardStyle}>
          {container.foodGradeAllowed && (
            <label style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "8px 0",
              minHeight: 44,
              cursor: container.foodGradeContainerType ? "default" : "pointer",
              WebkitTapHighlightColor: "transparent",
              opacity: container.foodGradeContainerType ? 0.7 : 1,
            }}>
              <input
                type="checkbox"
                checked={foodGrade}
                onChange={(e) => setFoodGrade(e.target.checked)}
                disabled={container.foodGradeContainerType}
                style={{ width: 22, height: 22, flexShrink: 0 }}
              />
              <span style={{ fontSize: 15 }}>Food Grade</span>
            </label>
          )}
          {container.kosherAllowed && (
            <label style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "8px 0",
              minHeight: 44,
              cursor: "pointer",
              WebkitTapHighlightColor: "transparent",
            }}>
              <input
                type="checkbox"
                checked={kosher}
                onChange={(e) => setKosher(e.target.checked)}
                style={{ width: 22, height: 22, flexShrink: 0 }}
              />
              <span style={{ fontSize: 15 }}>Kosher</span>
            </label>
          )}
        </div>
      )}

      <div style={bottomBarStyle}>
        <button onClick={onBack} style={{ ...btnSecondary, width: "40%" }}>
          Back
        </button>
        <button
          onClick={() =>
            canNext && onNext({ serviceTypeId, contentId, contentName: contentSearch, foodGrade, kosher })
          }
          style={canNext ? { ...btnPrimary, width: "60%" } : { ...btnDisabled, width: "60%" }}
          disabled={!canNext}
        >
          Next
        </button>
      </div>
    </div>
  );
}

// ─── Step 3: Service Plan ────────────────────────────────────────
function StepPlan({ container, serviceData, orgId, onNext, onBack }) {
  const [loading, setLoading] = useState(true);
  const [recommendedPlan, setRecommendedPlan] = useState(null);
  const [allPlans, setAllPlans] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState("");
  const [overrideReason, setOverrideReason] = useState("");
  const [overrideReasons, setOverrideReasons] = useState([]);
  const [specialInstructions, setSpecialInstructions] = useState("");
  const [gspError, setGspError] = useState(null);
  const [gspWarning, setGspWarning] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const data = await apiCall("/api/mwo/service-plan", {
          method: "POST",
          body: JSON.stringify({
            orgId,
            containerId: container.id,
            contentId: serviceData.contentId,
            serviceTypeId: serviceData.serviceTypeId,
            foodGrade: serviceData.foodGrade,
            kosher: serviceData.kosher,
          }),
        });
        if (data.status === "E") {
          setGspError(data.message || "No service plan found for this content and container.");
        } else if (data.status === "W") {
          setGspWarning(data.message || "Warning: check service plan.");
        }
        if (data.recommendedId) {
          setRecommendedPlan({ id: data.recommendedId, name: data.recommendedName, price: data.recommendedPrice });
          setSelectedPlanId(data.recommendedId);
        }
        setAllPlans(data.allPlans || []);
        setOverrideReasons(data.overrideReasons || []);
      } catch (err) {
        setGspError("Failed to get service plan: " + err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [orgId, container.id, serviceData]);

  const isOverridden = recommendedPlan && selectedPlanId && selectedPlanId !== recommendedPlan.id;
  const canNext = selectedPlanId && (!isOverridden || overrideReason);

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: 60 }}>
        <Spin size="large" />
        <div style={{ marginTop: 8, color: BRAND.textSecondary }}>Getting service plan...</div>
      </div>
    );
  }

  return (
    <div>
      {gspError && (
        <div
          style={{
            ...cardStyle,
            background: "#fef2f2",
            borderColor: BRAND.error,
            color: BRAND.error,
          }}
        >
          {gspError}
        </div>
      )}
      {gspWarning && (
        <div
          style={{
            ...cardStyle,
            background: "#fffbeb",
            borderColor: BRAND.warning,
            color: BRAND.warning,
          }}
        >
          {gspWarning}
        </div>
      )}

      {/* Recommended Plan */}
      {recommendedPlan && (
        <div style={cardStyle}>
          <label style={labelStyle}>Recommended Service Plan</label>
          <div style={{ fontSize: 15, fontWeight: 500, color: BRAND.primary, padding: "8px 0" }}>
            {recommendedPlan.name}
          </div>
        </div>
      )}

      {/* Selected Plan */}
      <div style={cardStyle}>
        <label style={labelStyle}>Selected Service Plan</label>
        <select
          value={selectedPlanId}
          onChange={(e) => setSelectedPlanId(e.target.value)}
          style={{ ...inputStyle, minHeight: 48 }}
        >
          <option value="">-- Select Plan --</option>
          {allPlans.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
      </div>

      {/* Override Reason */}
      {isOverridden && (
        <div style={cardStyle}>
          <label style={{ ...labelStyle, color: BRAND.warning }}>
            Override Reason (required)
          </label>
          <select
            value={overrideReason}
            onChange={(e) => setOverrideReason(e.target.value)}
            style={{ ...inputStyle, minHeight: 48, borderColor: !overrideReason ? BRAND.warning : BRAND.border }}
          >
            <option value="">-- Select Reason --</option>
            {overrideReasons.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Special Instructions */}
      <div style={cardStyle}>
        <label style={labelStyle}>Special Instructions</label>
        <textarea
          value={specialInstructions}
          onChange={(e) => setSpecialInstructions(e.target.value)}
          placeholder="Optional special instructions..."
          rows={3}
          style={{ ...inputStyle, resize: "vertical" }}
        />
      </div>

      <div style={bottomBarStyle}>
        <button onClick={onBack} style={{ ...btnSecondary, width: "40%" }}>
          Back
        </button>
        <button
          onClick={() =>
            canNext &&
            onNext({
              selectedPlanId,
              selectedPlanName: (allPlans.find((p) => p.id === selectedPlanId) || {}).name,
              recommendedPlanId: recommendedPlan ? recommendedPlan.id : selectedPlanId,
              overrideReason: isOverridden ? overrideReason : null,
              specialInstructions,
            })
          }
          style={canNext ? { ...btnPrimary, width: "60%" } : { ...btnDisabled, width: "60%" }}
          disabled={!canNext}
        >
          Next
        </button>
      </div>
    </div>
  );
}

// ─── Step 4: Review & Create ─────────────────────────────────────
function StepReview({ container, serviceData, planData, orgId, orgName, onBack, onSuccess }) {
  const [loading, setLoading] = useState(true);
  const [addresses, setAddresses] = useState([]);
  const [invoiceAddressId, setInvoiceAddressId] = useState("");
  const [shippingAddressId, setShippingAddressId] = useState("");
  const [customerPO, setCustomerPO] = useState("");
  const [poInfo, setPOInfo] = useState(null);
  const [reqDateOffsetHours, setReqDateOffsetHours] = useState(0);
  const [requestedDate, setRequestedDate] = useState(""); // datetime-local value (YYYY-MM-DDTHH:MM)
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        // Fetch addresses and PO info
        const data = await apiCall(`/api/mwo/review-data`, {
          method: "POST",
          body: JSON.stringify({
            containerId: container.id,
            orgId,
            bpId: container.ownerId,
          }),
        });
        setAddresses(data.addresses || []);
        setPOInfo(data.poInfo || null);
        setReqDateOffsetHours(data.reqDateOffsetHours || 0);

        // TSP-116: Calculate default requested date, rounded to the next hour (like Easy Button)
        if (data.reqDateOffsetHours > 0) {
          const d = new Date();
          d.setTime(d.getTime() + data.reqDateOffsetHours * 60 * 60 * 1000);
          d.setMinutes(0);
          d.setSeconds(0);
          d.setMilliseconds(0);
          // Format as datetime-local value: YYYY-MM-DDTHH:MM
          const pad = (n) => String(n).padStart(2, "0");
          const dtLocal = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
          setRequestedDate(dtLocal);
        }

        // Default invoice address
        if (data.defaultInvoiceAddressId) {
          setInvoiceAddressId(data.defaultInvoiceAddressId);
        } else if (data.addresses && data.addresses.length > 0) {
          setInvoiceAddressId(data.addresses[0].id);
        }

        // Default shipping address: owner terminal (TSP-79) or first address
        if (container.ownerTerminalId) {
          setShippingAddressId(container.ownerTerminalId);
        } else if (data.defaultShippingAddressId) {
          setShippingAddressId(data.defaultShippingAddressId);
        } else if (data.addresses && data.addresses.length > 0) {
          setShippingAddressId(data.addresses[0].id);
        }

        // Default PO
        if (data.defaultPO) {
          setCustomerPO(data.defaultPO);
        }
      } catch (err) {
        message.error("Failed to load review data: " + err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [container, orgId]);

  const handleCreate = async () => {
    setSubmitting(true);
    try {
      // TSP-116: Send requested date if set (from offset preference or user edit)
      let reqDateForApi = null;
      if (requestedDate) {
        reqDateForApi = requestedDate.replace("T", "T") + ":00"; // YYYY-MM-DDTHH:MM:00
      }

      const result = await apiCall("/api/mwo/create-order", {
        method: "POST",
        body: JSON.stringify({
          orgId,
          containerId: container.id,
          containerOwnerId: container.ownerId,
          contentId: serviceData.contentId,
          serviceTypeId: serviceData.serviceTypeId,
          servicePlanId: planData.selectedPlanId,
          recommendedPlanId: planData.recommendedPlanId,
          overrideReasonId: planData.overrideReason,
          specialInstructions: planData.specialInstructions,
          invoiceAddressId,
          shippingAddressId,
          customerPO,
          foodGrade: serviceData.foodGrade,
          kosher: serviceData.kosher,
          requestedDate: reqDateForApi,
        }),
      });
      if (result.status === "S") {
        onSuccess({
          workOrderId: result.workOrderId,
          documentNo: result.documentNo,
          salesOrderNo: result.salesOrderNo,
          etendoBaseUrl: result.etendoBaseUrl,
        });
      } else {
        message.error(result.message || "Failed to create order");
      }
    } catch (err) {
      message.error("Failed to create order: " + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: 60 }}>
        <Spin size="large" />
        <div style={{ marginTop: 8, color: BRAND.textSecondary }}>Loading review data...</div>
      </div>
    );
  }

  const canCreate = invoiceAddressId && shippingAddressId;

  return (
    <div>
      {/* Summary - compact 2-column green card */}
      <div style={{
        padding: "10px 14px",
        marginBottom: 12,
        fontSize: 12,
        background: BRAND.primary,
        color: "#fff",
        borderRadius: 12,
      }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4px 16px" }}>
          <SummaryRow label="Facility" value={orgName} light />
          <SummaryRow label="Container" value={container.searchkey} light />
          <SummaryRow label="BP" value={container.owner} light />
          <SummaryRow label="Content" value={serviceData.contentName} light />
          <SummaryRow label="Plan" value={planData.selectedPlanName} light />
          <SummaryRow label="Type" value={
            [serviceData.foodGrade ? "Food Grade" : "", serviceData.kosher ? "Kosher" : ""].filter(Boolean).join(", ") || "Standard"
          } light />
        </div>
        {planData.specialInstructions && (
          <div style={{ borderTop: "1px solid rgba(255,255,255,0.2)", paddingTop: 4, marginTop: 4 }}>
            <SummaryRow label="Instructions" value={planData.specialInstructions} light />
          </div>
        )}
      </div>

      {/* Customer PO - first since it's the field users need to fill */}
      <div style={cardStyle}>
        <label style={labelStyle}>Customer PO</label>
        <input
          type="text"
          value={customerPO}
          onChange={(e) => setCustomerPO(e.target.value)}
          placeholder={poInfo?.formatHelp || "Enter PO number"}
          style={inputStyle}
        />
        {poInfo?.formatHelp && (
          <div style={{ fontSize: 12, color: BRAND.textMuted, marginTop: 4 }}>
            Format: {poInfo.formatHelp}
          </div>
        )}
      </div>

      {/* Requested By Date - editable, shown only when offset preference is active */}
      {reqDateOffsetHours > 0 && (
        <div style={cardStyle}>
          <label style={labelStyle}>Requested By Date</label>
          <input
            type="datetime-local"
            value={requestedDate}
            onChange={(e) => setRequestedDate(e.target.value)}
            style={inputStyle}
          />
        </div>
      )}

      {/* Invoice Address */}
      <div style={cardStyle}>
        <label style={labelStyle}>Invoice Address</label>
        <select
          value={invoiceAddressId}
          onChange={(e) => setInvoiceAddressId(e.target.value)}
          style={{ ...inputStyle, minHeight: 48 }}
        >
          <option value="">-- Select --</option>
          {addresses.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
            </option>
          ))}
        </select>
      </div>

      {/* Shipping Address */}
      <div style={cardStyle}>
        <label style={labelStyle}>Service / Shipping Address</label>
        <select
          value={shippingAddressId}
          onChange={(e) => setShippingAddressId(e.target.value)}
          style={{ ...inputStyle, minHeight: 48 }}
        >
          <option value="">-- Select --</option>
          {addresses.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
            </option>
          ))}
        </select>
      </div>

      <div style={bottomBarStyle}>
        <button onClick={onBack} style={{ ...btnSecondary, width: "40%" }}>
          Back
        </button>
        <button
          onClick={handleCreate}
          style={
            canCreate && !submitting
              ? { ...btnPrimary, width: "60%", background: BRAND.success }
              : { ...btnDisabled, width: "60%", background: BRAND.success }
          }
          disabled={!canCreate || submitting}
        >
          {submitting ? "Creating..." : "Create Work Order"}
        </button>
      </div>
    </div>
  );
}

function SummaryRow({ label, value, light }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", fontSize: 14 }}>
      <span style={{ color: light ? "rgba(255,255,255,0.7)" : BRAND.textSecondary, flexShrink: 0, marginRight: 12 }}>{label}</span>
      <span style={{ fontWeight: 500, color: light ? "#fff" : BRAND.text, textAlign: "right", wordBreak: "break-word" }}>{value}</span>
    </div>
  );
}

// ─── Success Screen ──────────────────────────────────────────────
function SuccessScreen({ result, onAddAnother }) {
  let erpBaseFallback = "";
  try {
    const xhr = new XMLHttpRequest();
    xhr.open("GET", "/auth/config", false);
    xhr.send();
    if (xhr.status === 200) {
      const cfg = JSON.parse(xhr.responseText);
      if (cfg.erpBaseUrl) erpBaseFallback = cfg.erpBaseUrl;
    }
  } catch (e) { /* ignore */ }
  const erpBase = result.etendoBaseUrl || erpBaseFallback;
  const woLink = `${erpBase}/web/com.agilityerp.openbravo.mobile.quala.yardcheck/#wo-workorder&documentno=${result.documentNo}`;

  // TSP-121: Picture Upload launch. ContMng_WorkOrder ad_table_id is a fixed
  // Etendo table ID; safe to hardcode until the launch is generalized to more
  // record types.
  const WO_TABLE_ID = "74860304FAFD47BC815B03D4614BBE1A";
  const uploadHash = result.workOrderId
    ? `#/upload?tableId=${WO_TABLE_ID}&recordId=${encodeURIComponent(result.workOrderId)}&label=${encodeURIComponent("Work Order " + result.documentNo)}&returnUrl=${encodeURIComponent("/mwo")}`
    : null;

  return (
    <div style={{ textAlign: "center", padding: "48px 16px" }}>
      <div
        style={{
          width: 72,
          height: 72,
          borderRadius: "50%",
          background: "#d1fae5",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          margin: "0 auto 20px",
          fontSize: 36,
          color: BRAND.success,
        }}
      >
        {"\u2713"}
      </div>
      <div style={{ fontSize: 20, fontWeight: 700, color: BRAND.text, marginBottom: 12 }}>
        Work Order Created
      </div>
      <div style={{ fontSize: 16, color: BRAND.textSecondary, marginBottom: 24, lineHeight: 1.5 }}>
        Work Order{" "}
        <a href={woLink} target="_blank" rel="noopener noreferrer" style={{ fontWeight: 600, color: BRAND.primary }}>
          {result.documentNo}
        </a>{" "}
        has been created. Tap to open it.
      </div>
      {uploadHash && (
        <a
          href={uploadHash}
          style={{
            ...btnSecondary,
            marginTop: 8,
            textDecoration: "none",
            color: BRAND.primary,
            borderColor: BRAND.primary,
          }}
        >
          Add Pictures
        </a>
      )}
      <button onClick={onAddAnother} style={{ ...btnPrimary, marginTop: 12 }}>
        Add Another Work Order
      </button>
    </div>
  );
}

// ─── Main MWO Page ───────────────────────────────────────────────
export default function MWOPage() {
  const user = useContext(AuthContext);
  const [step, setStep] = useState(0);
  const [orgId, setOrgId] = useState("");
  const [orgName, setOrgName] = useState("");
  const [container, setContainer] = useState(null);
  const [serviceData, setServiceData] = useState(null);
  const [planData, setPlanData] = useState(null);
  const [result, setResult] = useState(null);

  // Parse URL params for optional containerId/orgId/contentId/bpId
  // When all 4 are provided, skip to Step 3 (Plan)
  const [urlParamsProcessed, setUrlParamsProcessed] = useState(false);
  useEffect(() => {
    if (urlParamsProcessed || !user?.facilities) return;
    const params = new URLSearchParams(window.location.hash.split("?")[1] || "");
    const urlOrg = params.get("orgId");
    const urlContainerId = params.get("containerId");
    const urlContentId = params.get("contentId");
    const urlBpId = params.get("bpId");

    // Resolve org from URL param: match by value ("101"), name ("Sidney OH"), or partial name
    let resolvedOrg = null;
    if (urlOrg) {
      const orgLower = urlOrg.toLowerCase();
      resolvedOrg = user.facilities.find((f) => f.value === urlOrg)
        || user.facilities.find((f) => f.name.toLowerCase() === orgLower)
        || user.facilities.find((f) => f.name.toLowerCase().startsWith(orgLower));
      if (resolvedOrg) {
        setOrgId(resolvedOrg.value);
        setOrgName(resolvedOrg.name);
      }
    }

    // URL param shortcuts:
    // orgId + containerId (no content): skip to Step 2 (Service)
    // orgId + containerId + contentId: skip to Step 3 (Plan)
    const effectiveOrg = resolvedOrg ? resolvedOrg.value : urlOrg;
    if (effectiveOrg && urlContainerId) {
      (async () => {
        try {
          // Fetch service types for container
          const svcTypes = await apiCall(`/api/mwo/service-types/${urlContainerId}`);
          const defaultSvcType = svcTypes.defaultServiceTypeId;

          // Validate container to get BP info (operated_by or owner)
          const validation = await apiCall("/api/mwo/validate-container", {
            method: "POST",
            body: JSON.stringify({ containerId: urlContainerId, orgId: effectiveOrg }),
          });

          // Try to find container (without WO filter so we always find it)
          const containerResult = await apiCall(`/api/mwo/containers?orgId=${effectiveOrg}&withoutWO=false&search=`);
          const containerMatch = containerResult.containers.find((c) => c.id === urlContainerId);

          // Build container object
          const containerObj = containerMatch || {
            id: urlContainerId,
            searchkey: validation.searchkey || urlContainerId.substring(0, 8),
            owner: validation.documentBpName || "Owner",
            ownerId: validation.documentBpId || urlBpId || "",
            containerType: validation.containerType || "",
            containerType: "",
            containerTypeId: "",
            compartments: 1,
          };
          // Use document BP (operated_by or owner) from validation
          if (validation.documentBpId) {
            containerObj.ownerId = validation.documentBpId;
            containerObj.owner = validation.documentBpName;
          }
          if (validation.ownerTerminalId) {
            containerObj.ownerTerminalId = validation.ownerTerminalId;
          }
          // Add service type info
          containerObj.serviceTypes = svcTypes.serviceTypes;
          containerObj.defaultServiceTypeId = defaultSvcType;
          containerObj.foodGradeAllowed = svcTypes.foodGradeAllowed;
          containerObj.foodGradeContainerType = svcTypes.foodGradeContainerType || false;
          containerObj.kosherAllowed = svcTypes.kosherAllowed;

          setContainer(containerObj);

          if (urlContentId) {
            // Content provided: skip to Step 3 (Plan)
            const contentResult = await apiCall(`/api/mwo/contents?id=${urlContentId}`);
            const contentMatch = contentResult.contents.length > 0 ? contentResult.contents[0] : null;
            const contentName = contentMatch ? contentMatch.name : "Selected Content";

            setServiceData({
              serviceTypeId: defaultSvcType,
              contentId: urlContentId,
              contentName: contentName,
              foodGrade: svcTypes.foodGradeContainerType || false,
              kosher: false,
            });
            setStep(2); // Plan step
          } else {
            // No content: skip to Step 2 (Service) with container pre-selected
            setStep(1); // Service step
          }
        } catch (err) {
          console.error("Failed to process URL params:", err);
        }
        setUrlParamsProcessed(true);
      })();
    } else {
      setUrlParamsProcessed(true);
    }
  }, [user, urlParamsProcessed]);

  // Default org if user has only one facility
  useEffect(() => {
    if (!orgId && user?.facilities?.length === 1) {
      setOrgId(user.facilities[0].value);
      setOrgName(user.facilities[0].name);
    }
  }, [user, orgId]);

  const resetWizard = () => {
    setStep(0);
    setContainer(null);
    setServiceData(null);
    setPlanData(null);
    setResult(null);
  };

  // Success screen
  if (result) {
    return (
      <div style={{
        maxWidth: 480,
        margin: "0 auto",
        padding: "0 16px",
        paddingBottom: "calc(16px + env(safe-area-inset-bottom, 0px))",
        minHeight: "100vh",
        boxSizing: "border-box",
      }}>
        <SuccessScreen result={result} onAddAnother={resetWizard} />
      </div>
    );
  }

  // Org selector if user has multiple facilities and none selected
  const needsOrgSelect = !orgId && user?.facilities?.length > 1;

  return (
    <div style={{
      maxWidth: 480,
      margin: "0 auto",
      padding: "0 16px",
      paddingBottom: "calc(16px + env(safe-area-inset-bottom, 0px))",
      minHeight: "100vh",
      boxSizing: "border-box",
    }}>
      {/* Page Title */}
      <div style={{ textAlign: "center", padding: "16px 0 4px" }}>
        <div style={{ fontSize: 20, fontWeight: 700, color: BRAND.text }}>
          Mobile Work Order
        </div>
        {orgName && (
          <div style={{ fontSize: 14, color: BRAND.textSecondary, marginTop: 4 }}>
            {orgName}
            {user?.facilities?.length > 1 && (
              <span
                onClick={() => {
                  setOrgId("");
                  setOrgName("");
                  resetWizard();
                }}
                style={{
                  marginLeft: 8,
                  color: BRAND.primary,
                  cursor: "pointer",
                  fontSize: 13,
                  fontWeight: 500,
                  padding: "4px 8px",
                }}
              >
                Change
              </span>
            )}
          </div>
        )}
      </div>

      {/* Show spinner while URL params are being processed */}
      {!urlParamsProcessed && window.location.hash.includes("containerId") ? (
        <div style={{ textAlign: "center", padding: 48 }}>
          <Spin size="large" />
          <div style={{ marginTop: 12, color: BRAND.textMuted }}>Loading...</div>
        </div>
      ) : needsOrgSelect ? (
        <div style={cardStyle}>
          <label style={labelStyle}>Select Facility</label>
          {user.facilities.map((f) => (
            <div
              key={f.value}
              onClick={() => {
                setOrgId(f.value);
                setOrgName(f.name);
              }}
              style={{
                padding: "16px 14px",
                minHeight: 52,
                display: "flex",
                alignItems: "center",
                borderBottom: `1px solid ${BRAND.border}`,
                cursor: "pointer",
                fontSize: 16,
                fontWeight: 500,
                WebkitTapHighlightColor: "transparent",
              }}
            >
              {f.name}
            </div>
          ))}
        </div>
      ) : (
        <>
          <StepIndicator current={step} />

          {/* TSP-116: Breadcrumb showing accumulated selections (hidden on Review step - summary replaces it) */}
          {container && step > 0 && step < 3 && (
            <div style={{
              padding: "10px 14px",
              marginBottom: 8,
              fontSize: 13,
              background: BRAND.primary,
              color: "#fff",
              borderRadius: 8,
              lineHeight: 1.6,
            }}>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span><strong>Container:</strong> {container.searchkey}</span>
                <span style={{ opacity: 0.85 }}>{container.owner}{container.containerType ? ` - ${container.containerType}` : ""}</span>
              </div>
              {serviceData && (
                <div style={{ display: "flex", justifyContent: "space-between", opacity: 0.85, borderTop: "1px solid rgba(255,255,255,0.2)", paddingTop: 4, marginTop: 4 }}>
                  <span><strong>Content:</strong> {serviceData.contentName}</span>
                  <span>
                    {serviceData.foodGrade ? "FG " : ""}{serviceData.kosher ? "K" : ""}
                  </span>
                </div>
              )}
            </div>
          )}

          {step === 0 && (
            <StepContainer
              orgId={orgId}
              orgName={orgName}
              onSelect={(c) => {
                setContainer(c);
                setStep(1);
              }}
            />
          )}

          {step === 1 && container && (
            <StepService
              container={container}
              orgId={orgId}
              onNext={(data) => {
                setServiceData(data);
                setStep(2);
              }}
              onBack={() => {
                setContainer(null);
                setStep(0);
              }}
            />
          )}

          {step === 2 && container && serviceData && (
            <StepPlan
              container={container}
              serviceData={serviceData}
              orgId={orgId}
              onNext={(data) => {
                setPlanData(data);
                setStep(3);
              }}
              onBack={() => setStep(1)}
            />
          )}

          {step === 3 && container && serviceData && planData && (
            <StepReview
              container={container}
              serviceData={serviceData}
              planData={planData}
              orgId={orgId}
              orgName={orgName}
              onBack={() => setStep(2)}
              onSuccess={setResult}
            />
          )}
        </>
      )}
    </div>
  );
}
